import React, { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ErrorState } from "@shared/common/components/State/ErrorState";
import { useKnopClient } from "@opstel/contexts/knopClient/useKnopClient";
import { TogglePinnedStateParameters, TrainUnitTransitions } from "./TrainUnitTransitions";
import { useCurrentInstanceId } from "@shared/common/contexts/instanceMetadata/useInstanceId";
import { instances } from "@opstel/query/queryKeys/instanceKeys";
import { messageToast } from "@shared/EDS/components/MessageToast/messageToastFunctions";

export function TrainUnitTransitionsWrapper(): React.JSX.Element {
  const { knopClient } = useKnopClient();
  const queryClient = useQueryClient();
  const { instanceId } = useCurrentInstanceId();
  const { data, error, isLoading } = useQuery({
    queryKey: instances.current(instanceId).planTrainUnitDetails,
    queryFn: () => knopClient.getPlanTrainUnitDetails(instanceId),
  });

  const toggleTransition = useMutation({
    mutationFn: ({ planTrainUnitGuid, newPinnedState }: TogglePinnedStateParameters) => {
      if (newPinnedState === "PINNED") {
        return knopClient.pinTransition({ instanceId: instanceId!, planTrainUnitGuid });
      }
      return knopClient.unpinTransition({ instanceId: instanceId!, planTrainUnitGuid });
    },
    onError: (err) => {
      console.error(err);
      messageToast({
        type: "error",
        message:
          "Materieelovergang opslaan is mislukt. Neem contact op met de applicatiebeheerder.",
      });
    },
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: instances.current(instanceId).planTrainUnitDetails,
      }),
  });

  const planTrainUnitDetails = useMemo(() => {
    if (!data) return [];
    if (!toggleTransition.isPending || !toggleTransition.variables) return data;
    // Optimistically update pinned state of mutated entry
    return data.map((details) => {
      if (details.ptuGuid !== toggleTransition.variables.planTrainUnitGuid) return details;
      return { ...details, matchingChangeability: toggleTransition.variables.newPinnedState };
    });
  }, [data, toggleTransition.isPending, toggleTransition.variables]);

  if (error) return <ErrorState />;
  return (
    <TrainUnitTransitions
      planTrainUnitDetails={planTrainUnitDetails}
      isLoading={isLoading}
      toggleTransition={() => toggleTransition.mutate}
    />
  );
}
